import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getDietaryData, saveDietaryForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { DIETARY } from '../../../formQuestionsEnum';
import DietaryDepartmentForm from './DietaryDepartmentForm';
import DietaryDirectorForm from './DietaryDirectorForm';
import FileUploadForm from './FileUploadForm';
import CurrentMenuSystemForm from './CurrentMenuSystemForm';
import DieticianForm from './DieticianForm';

const DietaryView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const dietaryPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.dietaryFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getDietaryData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(DIETARY).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await saveDietaryForm({
      id,
      sellerFacilityId: facilityId,
      leasedCompanyId: data.leasedCompanyId,
      rentalCompanyId: data.rentalCompanyId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Dietary data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Dietary data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <DietaryDepartmentForm permissions={dietaryPermissions} />
      <CurrentMenuSystemForm permissions={dietaryPermissions} />
      <FileUploadForm permissions={dietaryPermissions} />
      <DietaryDirectorForm permissions={dietaryPermissions} />
      <DieticianForm permissions={dietaryPermissions} />
    </>
  );
});

export default DietaryView;

DietaryView.propTypes = {
  setFormData: PropTypes.func,
};
